if (!customElements.get('predictive-search')) {
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.overlay = this.querySelector('[data-search-overlay]');
    this.modeSelector = this.querySelector('[data-search-mode]');
    this.loadingIndicator = this.querySelector('[data-search-loading]');
    this.form = this.querySelector('form');
    this.cachedResults = {};
    this.searchMode = this.modeSelector ? this.modeSelector.value : 'all';
    this.abortController = null;
    this.requestTimer = null;
    this.requestId = 0;
    this.resultsId = `predictive-search-results-${PredictiveSearch.instanceCount++}`;
    this.skuLabelTemplate = (window.theme && window.theme.strings && window.theme.strings.skuLabel) || 'SKU: __SKU__';
    this.skuPrefixLabelTemplate = (window.theme && window.theme.strings && window.theme.strings.skuPrefixLabel) || 'SKU starts with: __SKU__';

    if (!this.input || !this.resultsContainer) return;

    this.resultsContainer.id = this.resultsId;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.removeAttribute('aria-owns');

    this.setupEventListeners();

    // Shopify's full storefront search treats a field-qualified SKU fragment
    // as an exact value. If that page has no server-rendered results, reuse the
    // SKU-only predictive endpoint so incomplete SKUs still show useful items.
    if (this.usesSearchPageFallback() && this.input.value.trim().length > 0) {
      this.getSearchResults(this.input.value.trim());
    }
  }

  usesSearchPageFallback() {
    return (
      this.classList.contains('search-page-form') &&
      this.searchMode === 'sku' &&
      this.dataset.resultsCount === '0'
    );
  }

  setupEventListeners() {
    this.input.addEventListener(
      'input',
      this.debounce(() => this.onChange(), 300)
    );

    this.input.addEventListener('focus', () => {
      if (!this.usesSearchPageFallback() && this.input.value.trim().length > 0 && this.resultsContainer.innerHTML.trim().length > 0) {
        this.open();
      }
    });

    this.input.addEventListener('keydown', (e) => this.onInputKeydown(e));
    this.resultsContainer.addEventListener('keydown', (e) => this.onResultsKeydown(e));

    if (this.modeSelector) {
      this.modeSelector.addEventListener('change', (e) => {
        this.searchMode = e.target.value;
        this.cachedResults = {};
        const term = this.input.value.trim();
        if (term.length > 0) {
          this.getSearchResults(term);
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.usesSearchPageFallback()) this.close();
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) && !this.usesSearchPageFallback()) this.close();
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
  }

  onSubmit(e) {
    e.preventDefault();
    const term = this.input.value.trim();
    if (!term.length) {
      this.input.focus();
      return;
    }

    const params = new URLSearchParams();
    params.set('q', this.searchMode === 'sku' ? 'variants.sku:' + term : this.buildTitleQuery(term));
    const typeInput = this.form.querySelector('input[name="type"]');
    // SKU field queries only match inside product search, so always send it.
    if (this.searchMode === 'sku' || typeInput) {
      params.set('type', this.searchMode === 'sku' ? 'product' : typeInput.value);
    }
    window.location.href = this.form.getAttribute('action') + '?' + params.toString();
  }

  buildTitleQuery(term) {
    return term
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => `title:${word.replace(/([\\:()])/g, '\\$1')}`)
      .join(' ');
  }

  getResultLinks() {
    return Array.from(this.resultsContainer.querySelectorAll('.ps-item__link, .ps-view-all__link'));
  }

  onInputKeydown(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const links = this.getResultLinks();
      if (!links.length) return;
      e.preventDefault();
      links[e.key === 'ArrowDown' ? 0 : links.length - 1].focus();
    }
  }

  onResultsKeydown(e) {
    const links = this.getResultLinks();
    const index = links.indexOf(e.target.closest('a'));
    if (index < 0) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const offset = e.key === 'ArrowDown' ? 1 : -1;
      links[(index + offset + links.length) % links.length].focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.input.focus();
      this.close();
    }
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) {
      this.clearResults();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const cacheKey = `${this.searchMode}:${searchTerm}`;
    if (this.cachedResults[cacheKey]) {
      this.renderResults(this.cachedResults[cacheKey], searchTerm);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    const requestId = ++this.requestId;
    const controller = new AbortController();
    this.abortController = controller;

    this.showLoading();

    // Hard timeout: a hung /search/suggest request must never leave the
    // loader spinning behind the input bar.
    clearTimeout(this.requestTimer);
    this.requestTimer = setTimeout(() => {
      controller.abort();
    }, 8000);

    let queryTerm = searchTerm;
    let resourceTypes = 'product';
    let searchableFields = 'title';
    if (this.searchMode === 'sku') {
      // Predictive search applies prefix matching to the raw final term when
      // the searchable field is restricted to SKU. The qualified syntax is
      // retained for the submitted search page, where it means exact SKU.
      queryTerm = searchTerm;
      searchableFields = 'variants.sku';
    } else {
      resourceTypes = this.dataset.searchTypes || 'product';
    }

    const url = `/search/suggest?q=${encodeURIComponent(queryTerm)}&resources[type]=${resourceTypes}&resources[limit]=6&resources[options][fields]=${encodeURIComponent(searchableFields)}&section_id=predictive-search`;

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (requestId !== this.requestId) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML || '';
        this.cachedResults[cacheKey] = resultsMarkup;
        this.renderResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== this.requestId) return;
        this.clearResults();
      })
      .finally(() => {
        if (requestId === this.requestId) {
          clearTimeout(this.requestTimer);
          this.hideLoading();
        }
      });
  }

  renderResults(markup, searchTerm) {
    this.resultsContainer.innerHTML = markup;
    const idMap = {};
    this.resultsContainer.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = oldId === 'predictive-search-results' ? this.resultsId : `${this.resultsId}-${oldId}`;
      idMap[oldId] = newId;
      element.id = newId;
    });
    this.resultsContainer.querySelectorAll('[aria-labelledby]').forEach((element) => {
      const labelledBy = element.getAttribute('aria-labelledby');
      if (idMap[labelledBy]) element.setAttribute('aria-labelledby', idMap[labelledBy]);
    });

    if (this.searchMode === 'sku') {
      this.resultsContainer.querySelectorAll('[data-group="collections"], [data-group="pages"]').forEach(el => el.remove());
      this.highlightSkuMatches(searchTerm);

      if (this.usesSearchPageFallback()) {
        const hasMatches = this.resultsContainer.querySelector('.ps-item__link') !== null;
        this.renderSkuSearchPageResults(searchTerm);
        this.resultsContainer.innerHTML = '';
        this.close();
        if (!hasMatches) {
          return;
        }
        return;
      }
    }

    const viewAllLink = this.resultsContainer.querySelector('.ps-view-all__link');
    if (viewAllLink && this.form) {
      const params = new URLSearchParams();
      params.set('q', this.searchMode === 'sku' ? 'variants.sku:' + searchTerm : this.buildTitleQuery(searchTerm));
      const typeInput = this.form.querySelector('input[name="type"]');
      if (this.searchMode === 'sku' || typeInput) {
        params.set('type', this.searchMode === 'sku' ? 'product' : typeInput.value);
      }
      viewAllLink.href = `${this.form.getAttribute('action')}?${params.toString()}`;
    }

    this.open();
  }

  renderSkuSearchPageResults(searchTerm) {
    const searchPage = this.closest('.search-page');
    const pageResults = searchPage && searchPage.querySelector('[data-search-page-results]');
    if (!pageResults) return;

    const resultLinks = Array.from(this.resultsContainer.querySelectorAll('.ps-item__link'));
    const serverNoResults = pageResults.querySelector('[data-search-page-no-results]');
    if (!resultLinks.length) {
      if (serverNoResults) serverNoResults.hidden = false;
      return;
    }

    pageResults.innerHTML = '';
    pageResults.classList.add('sku-search-page-results');

    resultLinks.forEach((sourceLink) => {
      const cardColumn = document.createElement('div');
      cardColumn.className = 'col-md-3 col-sm-6 col-xs-6 collection-products-wrapper sku-search-page-result';

      const card = document.createElement('div');
      card.className = 'product-thumb';

      const link = document.createElement('a');
      link.className = 'product-thumb-href';
      link.href = sourceLink.href;

      const sourceTitle = sourceLink.querySelector('.ps-item__title');
      const titleText = sourceTitle ? sourceTitle.textContent.trim() : '';
      link.setAttribute('aria-label', titleText);

      const inner = document.createElement('div');
      inner.className = 'product-thumb-inner';

      const imageWrap = document.createElement('div');
      imageWrap.className = 'product-thumb-img-wrap';
      const sourceImage = sourceLink.querySelector('.ps-item__img');
      if (sourceImage) {
        const image = document.createElement('img');
        image.className = 'product-thumb-img lazyloaded';
        image.src = sourceImage.currentSrc || sourceImage.src;
        image.alt = sourceImage.alt || titleText;
        imageWrap.appendChild(image);
      }

      const caption = document.createElement('div');
      caption.className = 'product-thumb-caption';
      const title = document.createElement('h5');
      title.className = 'product-thumb-caption-title';
      title.textContent = titleText;

      const priceList = document.createElement('ul');
      priceList.className = 'product-thumb-caption-price';
      const priceItem = document.createElement('li');
      priceItem.className = 'product-thumb-caption-price-current';
      const price = document.createElement('span');
      price.className = 'money';
      const sourcePrice = sourceLink.querySelector('.ps-item__meta');
      price.textContent = sourcePrice ? sourcePrice.textContent.trim() : '';
      priceItem.appendChild(price);
      priceList.appendChild(priceItem);

      const skuPrefix = document.createElement('p');
      skuPrefix.className = 'product-thumb-caption-desc sku-search-prefix';
      skuPrefix.textContent = this.formatSkuPrefixLabel(searchTerm);

      caption.appendChild(title);
      caption.appendChild(priceList);
      caption.appendChild(skuPrefix);
      inner.appendChild(imageWrap);
      inner.appendChild(caption);
      card.appendChild(link);
      card.appendChild(inner);
      cardColumn.appendChild(card);
      pageResults.appendChild(cardColumn);
    });
  }

  formatSkuPrefixLabel(sku) {
    return this.skuPrefixLabelTemplate.replace('__SKU__', sku);
  }

  highlightSkuMatches(searchTerm) {
    const items = this.resultsContainer.querySelectorAll('[data-sku-match]');
    items.forEach((el) => {
      el.style.display = 'block';
      el.textContent = this.formatSkuPrefixLabel(searchTerm);
    });
  }

  open() {
    this.resultsContainer.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
  }

  close() {
    if (this.usesSearchPageFallback() && this.resultsContainer.innerHTML.trim().length > 0) return;
    this.resultsContainer.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    this.classList.remove('is-page-results');
  }

  clearResults() {
    this.requestId++;
    if (this.abortController) {
      this.abortController.abort();
    }
    clearTimeout(this.requestTimer);
    this.resultsContainer.innerHTML = '';
    this.close();
    this.hideLoading();
  }

  // Called by the search popup controller when the popup opens or closes so
  // stale results never survive into the next session.
  reset() {
    this.searchMode = 'all';
    if (this.modeSelector) this.modeSelector.value = 'all';
    this.cachedResults = {};
    this.clearResults();
  }

  showLoading() {
    if (this.loadingIndicator) this.loadingIndicator.classList.add('is-visible');
  }

  hideLoading() {
    if (this.loadingIndicator) this.loadingIndicator.classList.remove('is-visible');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

PredictiveSearch.instanceCount = 1;

// Both the header popup and the search page render this script. Keeping the
// declaration inside this guard makes loading the asset twice safe as well.
  customElements.define('predictive-search', PredictiveSearch);
}
